'use no memo';

import type { AssignmentListItem } from '@repo/types/assignments';
import type { ColumnDef, RowSelectionState } from '@tanstack/react-table';

import { useGetAssignedAssignments } from '@repo/api/assignment/useGetAssignedAssignments';
import { endOfDay, format, isAfter, isValid, isWithinInterval, parseISO, startOfDay } from 'date-fns';
import { EditIcon, Trash2Icon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';

import DeleteAssignment from '@/components/features/manage/manage-assignments/DeleteAssignment';
import TanstackTable from '@/components/features/manage/tanstack-table/TanstackTable';
import { useTanstackTable } from '@/components/features/manage/tanstack-table/useTanstackTable';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useTableRowSelection } from '@/hooks/table';

export default function ManageAssignmentsPage() {
  const navigate = useNavigate();

  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [activeAssignment, setActiveAssignment] = useState<AssignmentListItem | undefined>(undefined);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const { data = [], isPending } = useGetAssignedAssignments();

  const columns = useMemo<ColumnDef<AssignmentListItem>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllPageRowsSelected() ? true : table.getIsSomePageRowsSelected() ? 'indeterminate' : false}
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => <Checkbox checked={row.getIsSelected()} onCheckedChange={(value) => row.toggleSelected(!!value)} aria-label={'Select row'} />,
        enableSorting: false,
        enableHiding: false,
      },
      {
        meta: 'Nazwa',
        id: 'name',
        accessorKey: 'name',
        header: 'Nazwa',
      },
      {
        meta: 'Typ',
        id: 'type',
        accessorKey: 'type',
        header: 'Typ',
        cell: ({ getValue }) => (getValue() === 'quiz' ? 'Quiz' : 'Zadanie'),
        filterFn: (row, id, value) => {
          if (!value || !Array.isArray(value) || value.length === 0) return true;
          return value.includes(row.getValue(id));
        },
      },
      {
        meta: 'Dostępność',
        id: 'availability',
        header: 'Dostępność',
        cell: ({ row }) => {
          const { available, dueTo } = row.original;

          if (
            isWithinInterval(new Date(), {
              start: available ?? new Date('1000-01-01'),
              end: dueTo ?? new Date('9999-12-31'),
            })
          )
            return <Badge variant={'success'}>Aktywne</Badge>;

          if (dueTo && isAfter(new Date(), dueTo)) return <Badge variant={'destructive'}>Po terminie</Badge>;

          return <Badge variant={'blue'}>Nie rozpoczęte</Badge>;
        },
        filterFn: (row, _columnId, filterValue) => {
          const { available, dueTo } = row.original;
          const now = new Date();

          const state = isWithinInterval(now, {
            start: available ?? new Date('1000-01-01'),
            end: dueTo ?? new Date('9999-12-31'),
          })
            ? 'Aktywne'
            : dueTo && isAfter(now, dueTo)
              ? 'Po terminie'
              : 'Nie rozpoczęte';

          return filterValue.includes(state);
        },
      },
      {
        meta: 'Liczba podejść',
        id: 'maxAttempts',
        accessorKey: 'maxAttempts',
        header: 'Liczba podejść',
        cell: ({ getValue }) => {
          const value = getValue();
          return typeof value === 'number' && value > 0 ? value : 'Bez limitu';
        },
      },
      {
        meta: 'Dostępne od',
        id: 'available',
        accessorKey: 'available',
        sortType: 'datetime',
        cell: ({ getValue }) => {
          const date = getValue();

          if (date == null) return <p>-</p>;

          if (typeof date === 'string' && isValid(parseISO(date))) {
            return format(new Date(date), 'HH:mm dd.MM.yyyy');
          }
          return date;
        },
        header: 'Dostępne od',
        filterFn: (row, id, value) => {
          if (!value || (!value.start && !value.end)) return true;
          const dateStr = row.getValue(id) as string | null;
          if (!dateStr || !isValid(parseISO(dateStr))) return false;
          const date = parseISO(dateStr);
          const start = value.start ? new Date(value.start) : null;
          const end = value.end ? new Date(value.end) : null;
          if (start && end) {
            return date >= start && date <= end;
          }
          if (start) {
            return date >= start;
          }
          if (end) {
            return date <= end;
          }
          return true;
        },
      },
      {
        meta: 'Termin',
        id: 'dueTo',
        accessorKey: 'dueTo',
        sortType: 'datetime',
        cell: ({ getValue }) => {
          const date = getValue();

          if (date == null) return <p>-</p>;

          if (typeof date === 'string' && isValid(parseISO(date))) {
            return format(new Date(date), 'HH:mm dd.MM.yyyy');
          }
          return date;
        },
        header: 'Termin',
        filterFn: (row, id, value) => {
          if (!value || (!value.start && !value.end)) return true;
          const dateStr = row.getValue(id) as string | null;
          if (!dateStr || !isValid(parseISO(dateStr))) return false;
          const date = parseISO(dateStr);
          const start = value.start ? new Date(value.start) : null;
          const end = value.end ? new Date(value.end) : null;
          if (start && end) {
            return date >= start && date <= end;
          }
          if (start) {
            return date >= start;
          }
          if (end) {
            return date <= end;
          }
          return true;
        },
      },
      {
        meta: 'Data utworzenia',
        id: 'createdAt',
        accessorKey: 'createdAt',
        sortType: 'datetime',
        cell: ({ getValue }) => {
          const date = getValue();
          if (typeof date === 'string' && isValid(parseISO(date))) {
            return format(new Date(date), 'HH:mm dd.MM.yyyy');
          }
          return date;
        },
        header: 'Data utworzenia',
        filterFn: (row, id, value) => {
          if (!value || (!value.start && !value.end)) return true;
          const dateStr = row.getValue(id) as string | null;
          if (!dateStr || !isValid(parseISO(dateStr))) return false;
          const date = startOfDay(parseISO(dateStr));
          const start = value.start ? startOfDay(new Date(value.start)) : null;
          const end = value.end ? endOfDay(new Date(value.end)) : null;
          if (start && end) {
            return date >= start && date <= end;
          }
          if (start) {
            return date >= start;
          }
          if (end) {
            return date <= end;
          }
          return true;
        },
      },
      {
        id: 'actions',
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => {
          const rowOrigin = row.original;

          return (
            <div className={'flex w-full justify-end'}>
              <Button
                size={'sm'}
                variant={'ghost'}
                variantColor={'destructive'}
                onClick={() => {
                  setActiveAssignment(rowOrigin);
                  setOpenDelete(true);
                }}
              >
                <Trash2Icon />
              </Button>
              <Button size={'sm'} variant={'ghost'} variantColor={'default'} asChild>
                <Link to={`builder/${rowOrigin.id}`}>
                  <EditIcon />
                </Link>
              </Button>
            </div>
          );
        },
      },
    ],
    []
  );

  const { table, setGlobalFilter } = useTanstackTable({
    name: 'assignments',
    columns,
    data,
    getRowId: (row) => String(row.id),
    rowSelection,
    onRowSelectionChange: setRowSelection,
    defaultColumnVisibility: {
      available: false,
      createdAt: false,
    },
  });

  const selectedAssignments = useMemo<AssignmentListItem[]>(() => {
    const selectedIds = Object.keys(rowSelection);
    if (selectedIds.length === 0) return [];
    const idSet = new Set(selectedIds);
    return data.filter((assignment) => idSet.has(String(assignment.id)));
  }, [rowSelection, data]);

  const { handleRowClick } = useTableRowSelection({
    table,
    setRowSelection,
  });

  return (
    <>
      <DeleteAssignment
        assignments={activeAssignment ? [activeAssignment] : selectedAssignments}
        open={openDelete}
        onOpenChange={(open) => {
          setOpenDelete(open);
          if (!open) setActiveAssignment(undefined);
        }}
      />
      <div className={'base-direction-col h-full'}>
        <div className={'viewport-height flex w-full flex-col gap-4'}>
          <TanstackTable
            table={table}
            label={'Zadania'}
            isLoading={isPending}
            onGlobalFilterChange={setGlobalFilter}
            filterBy={[
              {
                id: 'type',
                title: 'Typ',
                type: 'faceted',
                options: [
                  {
                    label: 'Zadanie',
                    value: 'regular',
                  },
                  {
                    label: 'Quiz',
                    value: 'quiz',
                  },
                ],
              },
              {
                id: 'availability',
                title: 'Dostępność',
                type: 'faceted',
                options: [
                  {
                    label: 'Aktywne',
                    value: 'Aktywne',
                  },
                  {
                    label: 'Po terminie',
                    value: 'Po terminie',
                  },
                  {
                    label: 'Nie rozpoczęte',
                    value: 'Nie rozpoczęte',
                  },
                ],
              },
              {
                id: 'available',
                title: 'Dostępne od',
                type: 'dateRange',
              },
              {
                id: 'dueTo',
                title: 'Termin',
                type: 'dateRange',
              },
              {
                id: 'createdAt',
                title: 'Data utworzenia',
                type: 'dateRange',
              },
            ]}
            onRemoveSelectedRows={() => setOpenDelete(true)}
            onRowClick={handleRowClick}
            actionsComponent={
              <Button size={'md'} onClick={() => navigate('/manage/assignments/builder')}>
                Utwórz zadanie
              </Button>
            }
          />
        </div>
      </div>
    </>
  );
}
